// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Modal, Dropdown, TextInput } from '@loa/ui-components';

type ArchiveClientModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { relationshipName: string; crmRelationshipNumber: string }) => void;
  loading?: boolean;
}>;

export function ArchiveClientModal({
  open,
  onClose,
  onConfirm,
  loading = false,
}: ArchiveClientModalProps) {
  const [relationshipName, setRelationshipName] = useState('');
  const [crmRelationshipNumber, setCrmRelationshipNumber] = useState('');

  // Reset the form whenever the modal is opened/closed.
  useEffect(() => {
    if (!open) {
      setRelationshipName('');
      setCrmRelationshipNumber('');
    }
  }, [open]);

  const handleCancel = () => {
    setRelationshipName('');
    setCrmRelationshipNumber('');
    onClose();
  };

  const canArchive = relationshipName.trim().length > 0 && !loading;

  return (
    <Modal open={open} onClose={handleCancel} size="large" variant="general">
      <Modal.Header icon="archive drawer" title="Archive Client" onClose={handleCancel} />
      <Modal.Content>
        <div className="mb-xl text-[#000] font-sans text-body-01 font-normal leading-normal">
          By archiving this client from a joint relationship,{' '}
          <span className="font-bold">it changes to a solo relationship</span>. Please update the
          relationship name and CRM number to reflect this. The existing data will not be lost.
        </div>

        <div className="flex flex-col gap-lg">
          <Dropdown
            label="Relationship type"
            required
            options={[{ value: 'SOLO', text: 'Solo' }]}
            value="SOLO"
            disabled
            fluid
          />

          <TextInput
            label="Relationship name"
            value={relationshipName}
            onChange={(e) => setRelationshipName(e.target.value)}
            maxLength={255}
            required
            autoFocus
            fluid
          />

          <TextInput
            label="CRM relationship number"
            value={crmRelationshipNumber}
            onChange={(e) => setCrmRelationshipNumber(e.target.value)}
            maxLength={100}
            fluid
          />
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleCancel, disabled: loading, colour: 'red' }}
        primaryAction={{
          label: 'Archive Client',
          onClick: () => onConfirm({ relationshipName, crmRelationshipNumber }),
          colour: 'red',
          loading,
          disabled: !canArchive,
        }}
      />
    </Modal>
  );
}
